import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";

import { resolveUploadUrl } from "../../../../infrastructure/utils/resolveUploadUrl";

const ZOOM_STEPS = [1, 1.25, 1.5, 2, 2.5];

const FilePreviewLightbox = ({ open, onClose, label, url, fileBaseUrl = "" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [zoomIndex, setZoomIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const fullSrc = useMemo(
    () => (open && url ? resolveUploadUrl(url, fileBaseUrl) : ""),
    [open, url, fileBaseUrl]
  );

  useEffect(() => {
    if (open) {
      setZoomIndex(0);
      setLoading(Boolean(fullSrc));
      setFailed(false);
    }
  }, [open, fullSrc]);

  const zoom = ZOOM_STEPS[zoomIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      BackdropProps={{
        sx: { bgcolor: "rgba(0, 0, 0, 0.88)" },
      }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "hidden",
          m: 0,
          maxWidth: "100vw",
          maxHeight: "100vh",
          width: isMobile ? "100%" : "min(96vw, 1200px)",
          height: isMobile ? "100%" : "min(92vh, 900px)",
        },
      }}
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
    >
      <Stack
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          minHeight: isMobile ? "100vh" : "70vh",
          p: { xs: 1, sm: 2 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1, flexShrink: 0 }}
        >
          <Typography variant="subtitle2" color="common.white" noWrap sx={{ pr: 1 }}>
            {label || "پیش‌نمایش"}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              aria-label="بزرگ‌نمایی"
              onClick={() =>
                setZoomIndex((i) => Math.min(i + 1, ZOOM_STEPS.length - 1))
              }
              disabled={zoomIndex >= ZOOM_STEPS.length - 1 || failed}
              sx={{ color: "common.white" }}
              size="small"
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              aria-label="کوچک‌نمایی"
              onClick={() => setZoomIndex((i) => Math.max(i - 1, 0))}
              disabled={zoomIndex <= 0 || failed}
              sx={{ color: "common.white" }}
              size="small"
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              aria-label="بستن پیش‌نمایش"
              onClick={onClose}
              sx={{ color: "common.white" }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
            minHeight: 0,
          }}
        >
          {loading && !failed ? (
            <CircularProgress sx={{ color: "common.white" }} size={36} />
          ) : null}
          {failed ? (
            <Typography color="common.white">بارگذاری تصویر ممکن نشد.</Typography>
          ) : null}
          {fullSrc && !failed ? (
            <Box
              component="img"
              src={fullSrc}
              alt={label}
              onLoad={() => setLoading(false)}
              onError={() => {
                setLoading(false);
                setFailed(true);
              }}
              sx={{
                maxWidth: "100%",
                maxHeight: isMobile ? "calc(100vh - 96px)" : "calc(92vh - 80px)",
                objectFit: "contain",
                transform: `scale(${zoom})`,
                transition: "transform 0.2s ease",
                display: loading ? "none" : "block",
              }}
            />
          ) : null}
        </Box>
      </Stack>
    </Dialog>
  );
};

FilePreviewLightbox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
  url: PropTypes.string,
  fileBaseUrl: PropTypes.string,
};

export default FilePreviewLightbox;
